import { CPLAT_API_ROUTES, sellerQueries, usePost } from '@/api';
import DeleteButtonIcon from '@/assets/icons/DeleteButtonIcon.svg?react';
import { useAppSelector } from '@/redux/store';
import { CplatApiResponse } from '@/types/api';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronRightIcon } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import z from 'zod';
import { Dialog, DialogClose, DialogContent, DialogTrigger } from '../atoms/Dialog';
import { Button } from '../atoms/NewPcButton';
import Input from '../V3/Input';
import { useToast } from '@/hooks/V3/useToast';

const formSchema = z.object({ nickname: z.string().min(1) });

type FormType = z.infer<typeof formSchema>;

interface Props {
  defaultValue?: string;
}

export const NicknameDialog = ({ defaultValue }: Props) => {
  const queryClient = useQueryClient();

  const user = useAppSelector((state) => state.user);
  const cplatToken = user.CplatToken;

  const [open, setOpen] = useState(false);

  const { showToast } = useToast();

  const {
    register,
    watch,
    setValue,
    formState: { isValid, errors },
    handleSubmit,
    setError,
  } = useForm<FormType>({
    resolver: zodResolver(formSchema),
    shouldUnregister: true,
  });

  const watchedNickname = watch('nickname');

  const { mutate: checkDuplicateStoreName } = usePost(CPLAT_API_ROUTES.hasStoreName);
  const { mutate: onUpdateInfo } = usePost(CPLAT_API_ROUTES.updateInfo, undefined, undefined, true);

  const handleCheckNickname = (values: FormType) => {
    checkDuplicateStoreName(
      { storeName: values.nickname },
      {
        onSuccess(res) {
          const { code } = res.data as CplatApiResponse<string>;

          // 중복이 아닐 경우
          if (code === '200') {
            const body = { cplatToken, sellerName: values.nickname };

            onUpdateInfo(body, {
              onSuccess: () => {
                setOpen(false);
                queryClient.invalidateQueries({ queryKey: sellerQueries.all() });
                showToast('success', '닉네임 수정이 완료되었어요');
              },
              onError: () => showToast('warning', '일시적 오류로 변경을 실패했어요'),
            });
          }

          // 중복일 경우
          if (code === '614') {
            setError('nickname', { message: '이미 사용중인 닉네임입니다.' });
          }
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger className="flex w-full items-center justify-between text-gr-700 Body2M16">
        <span className="text-gr-700 Body2M16">닉네임</span>
        <div className="flex items-center ">
          {defaultValue}
          <ChevronRightIcon className="h-5 w-5 text-gr-400" />
        </div>
      </DialogTrigger>
      <DialogContent className="w-[420px] gap-5" noCloseBtn>
        <div className="space-y-1">
          <div className="text-gr-900 Title3S18">닉네임 수정</div>
          <div className="text-gr-700 Body5M14">변경할 닉네임을 입력해주세요</div>
        </div>
        <Input
          placeholder={defaultValue}
          InputProps={{
            endAdornment: watchedNickname && (
              <button onClick={() => setValue('nickname', '')}>
                <DeleteButtonIcon />
              </button>
            ),
          }}
          Error={{ isError: !!errors.nickname?.message, errorMessage: errors.nickname?.message }}
          {...register('nickname')}
        />
        <div className="flex gap-3">
          <DialogClose asChild>
            <Button variant="secondary" className="flex-1">
              취소
            </Button>
          </DialogClose>
          <Button
            className="flex-1"
            disabled={!isValid}
            onClick={handleSubmit(handleCheckNickname)}
          >
            수정
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
